import { useCallback, useEffect, useState } from 'react';
import { useMap } from './useMap';
import { useStickyState } from '../../../hooks/useStickyState';

const RECENT_RESULTS_STORAGE_KEY = 'rwl:autocomplete:recent';

type AutocompleteSuggestion = google.maps.places.QueryAutocompletePrediction;

type UsePlacesAutocompleteServiceArgs = {
    recentResultsLength?: number;
};

export const usePlacesAutocompleteService = ({
    recentResultsLength = 3,
}: UsePlacesAutocompleteServiceArgs = {}) => {
    const { isGMScriptLoaded } = useMap();
    const [placesService, setPlacesService] =
        useState<google.maps.places.AutocompleteService | null>(null);
    const [geocodingService, setGeocodingService] =
        useState<google.maps.Geocoder | null>(null);
    const [query, setQuery] = useState<string | null>(null);
    const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>(
        []
    );
    const [recentResults, setRecentResults] = useStickyState<
        AutocompleteSuggestion[]
    >([], RECENT_RESULTS_STORAGE_KEY);
    const [selectedPlaceId, setSelectedPlaceId] = useState<string | undefined>(
        undefined
    );
    const [selectedPlaceDetails, setSelectedPlaceDetails] =
        useState<google.maps.GeocoderResult | null>(null);

    useEffect(() => {
        if (isGMScriptLoaded) {
            setPlacesService(new google.maps.places.AutocompleteService());
            setGeocodingService(new google.maps.Geocoder());
        }
    }, [isGMScriptLoaded]);

    // TODO: Wrap in useQuery to benefit from local caching
    useEffect(() => {
        if (query === 'Current location') {
            return;
        }
        if (placesService && query?.length) {
            placesService.getPlacePredictions(
                {
                    input: query,
                    componentRestrictions: { country: ['us', 'ca'] },
                },
                (results, status) => {
                    if (status !== google.maps.places.PlacesServiceStatus.OK) {
                        setSuggestions([]);
                        return;
                    }
                    setSuggestions(results ?? []);
                }
            );
        }
    }, [placesService, query]);

    // TODO: Wrap in useQuery to benefit from local caching
    useEffect(() => {
        if (geocodingService) {
            if (!selectedPlaceId) {
                setSelectedPlaceDetails(null);
                return;
            }
            geocodingService.geocode(
                { placeId: selectedPlaceId },
                (results, status) => {
                    if (status !== google.maps.GeocoderStatus.OK) {
                        return;
                    }
                    const result = results?.[0];
                    if (!result) {
                        return;
                    }
                    setSelectedPlaceDetails(result);
                }
            );
        }
    }, [geocodingService, selectedPlaceId]);

    const addRecentResult = useCallback(
        (result: AutocompleteSuggestion) =>
            setRecentResults((prev: AutocompleteSuggestion[]) => {
                // using a map to dedupe the results by place_id
                const asMap = new Map<string, AutocompleteSuggestion>(
                    prev.map((r) => [r.place_id ?? r.description, r])
                );
                asMap.set(result.place_id ?? result.description, result);
                return Array.from(asMap.values())
                    .slice(-recentResultsLength)
                    .reverse();
            }),
        [setRecentResults, recentResultsLength]
    );

    return {
        setQuery,
        suggestions,
        recentResults,
        addRecentResult,
        setSelectedPlaceId,
        selectedPlaceDetails,
        geocodingService,
    };
};
``;
